import { computed, inject, isRef } from "vue";
import { PassportHeader } from "@/SharedComponents/Passport";
import {
    AssetOverview,
    AssetGroupOverview,
    SpaceOverview,
    Tasks,
    Files,
    Assets,
    Information,
    Property,
    ScannedActions,
    QrCodes,
} from "@/SharedComponents/Passport/Steps";
import { ProfileImage } from "@/SharedComponents/Passport/Steps/Images";
import { PropertyType } from "@/Composables/useProperties";
import { Steps } from "@/Composables/useSteps";

export function usePassport(storeKey, item) {
    const _storeKey = computed(() =>
        isRef(storeKey) ? storeKey.value : storeKey
    );

    const _item = computed(() => (isRef(item) ? item.value : item));

    const building = computed(() => {
        return _item.value.tenant;
    });

    const { hasPermission, permissions } = inject("permissions");

    const canUpdate = computed(() => {
        switch (_storeKey.value) {
            case "assets":
                return hasPermission(
                    permissions.updateAsset,
                    building.value.id
                );
            case "asset-groups":
                return hasPermission(
                    permissions.updateAsset,
                    building.value.id
                );
            case "spaces":
                return hasPermission(
                    permissions.updateSpace,
                    building.value.id
                );
            default:
                return false;
        }
    });

    const canDelete = computed(() => {
        switch (_storeKey.value) {
            case "assets":
                return hasPermission(
                    permissions.deleteAsset,
                    building.value.id
                );
            case "asset-groups":
                return hasPermission(
                    permissions.deleteAsset,
                    building.value.id
                );
            case "spaces":
                return hasPermission(
                    permissions.deleteSpace,
                    building.value.id
                );
            default:
                return false;
        }
    });

    const overviewContent = computed(() => {
        switch (_storeKey.value) {
            case "assets":
                return AssetOverview;
            case "asset-groups":
                return AssetGroupOverview;
            case "spaces":
                return SpaceOverview;
            default:
                return undefined;
        }
    });

    const properties = computed(() => {
        switch (_storeKey.value) {
            case "assets":
                return [
                    PropertyType.SPACE,
                    PropertyType.ASSET_GROUP,
                    PropertyType.FILES,
                    PropertyType.TASKS,
                    PropertyType.QR_CODES,
                ];
            case "asset-groups":
                return [
                    PropertyType.FILES,
                    PropertyType.ASSET_GROUP,
                    PropertyType.TASKS,
                    PropertyType.ASSETS,
                ];
            case "spaces":
                return [
                    PropertyType.SPACE,
                    PropertyType.FILES,
                    PropertyType.TASKS,
                    PropertyType.ASSETS,
                    PropertyType.QR_CODES,
                ];
            default:
                return [];
        }
    });

    const propertyStep = computed(() => ({
        header: PassportHeader,
        headerProps: {
            type: _storeKey.value,
            text: _item.value.name,
        },
    }));

    const allSteps = computed(() => ({
        [PropertyType.SPACE]: {
            ...propertyStep.value,
            content: Property,
            props: {
                item: _item.value,
                typeKey: PropertyType.SPACE,
                disabledItems:
                    _storeKey.value === "spaces" ? [_item.value.id] : [],
                withSearch: true,
            },
        },
        [PropertyType.ASSET_GROUP]: {
            ...propertyStep.value,
            content: Property,
            props: {
                item: _item.value,
                disabledItems:
                    _storeKey.value === "asset-groups" ? [_item.value.id] : [],
                typeKey: PropertyType.ASSET_GROUP,
                withSearch: true,
            },
        },
        [PropertyType.TASKS]: {
            ...propertyStep.value,
            content: Tasks,
            props: {
                item: _item.value,
                filter: {
                    space: [_item.value.id],
                },
                building: building.value,
            },
        },
        [PropertyType.FILES]: {
            ...propertyStep.value,
            content: Files,
            props: {
                item: _item.value,
                building: building.value,
                canUpdate: canUpdate.value,
            },
        },
        [PropertyType.ASSETS]: {
            ...propertyStep.value,
            content: Assets,
            props: {
                item: _item.value,
                filter: {
                    [_storeKey.value === "spaces" ? "space" : "asset_groups"]: [
                        _item.value.id,
                    ],
                },
                building: building.value,
                canUpdate: canUpdate.value,
            },
        },
        [PropertyType.QR_CODES]: {
            ...propertyStep.value,
            content: QrCodes,
            props: {
                item: _item.value,
                type: _storeKey.value,
                building: building.value,
                canUpdate: canUpdate.value,
            },
        },
    }));

    const steps = computed(
        () =>
            new Steps({
                overview: {
                    header: PassportHeader,
                    headerProps: {
                        type: "building",
                        text: building.value.name,
                        imageUrl: building.value.logo_url,
                        canDelete: canDelete.value,
                        item: _item.value,
                        itemType: _storeKey.value,
                    },
                    content: overviewContent.value,
                    props: {
                        item: _item.value,
                        building: building.value,
                        canUpdate: canUpdate.value,
                        scanned: _item.value.scanned,
                    },
                    footer: ScannedActions,
                    footerProps: {
                        item: _item.value,
                        building: building.value,
                        canUpdateAsset: hasPermission(
                            permissions.updateAsset,
                            building.value.id
                        ),
                        canUpdateSpace: hasPermission(
                            permissions.updateSpace,
                            building.value.id
                        ),
                    },
                },
                info: {
                    ...propertyStep.value,
                    content: Information,
                    props: {
                        item: _item.value,
                        type: _storeKey.value,
                        canUpdate: canUpdate.value,
                    },
                },
                profile_image: {
                    ...propertyStep.value,
                    content: ProfileImage,
                    props: {
                        item: _item.value,
                    },
                },
                ...Object.fromEntries(
                    new Map(
                        properties.value.map((key) => [
                            key,
                            allSteps.value[key],
                        ])
                    )
                ),
            })
    );

    return {
        steps,
    };
}
